import { Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { FoodDto } from "../../../shared/foods";
import { foodApi } from "../../api/queries";
import { ICON } from "../../components/ui";

// Ingredient search of the food database (recipe block, "Ingrediente de completat"). Romanian works with or
// without diacritics; an English food found through a Romanian word shows that word.

export function foodLabel(f: FoodDto): string {
  return [
    f.name,
    f.brand ? ` · ${f.brand}` : "",
    f.hint ? ` — ${f.hint}` : "",
    f.kind === "base"
      ? " (preparat de bază)"
      : f.kind === "food"
        ? " (aliment de bază)"
        : f.needsLabel
          ? " (de verificat pe etichetă)"
          : "",
  ].join("");
}

export function FoodSearch({
  rid,
  id,
  label,
  query,
  onQuery,
  onPick,
  autoFocus,
}: {
  rid: string;
  id: string;
  label: string;
  query: string;
  onQuery: (q: string) => void;
  onPick: (f: FoodDto) => void;
  autoFocus?: boolean;
}) {
  const api = useMemo(() => foodApi(rid), [rid]);
  const [results, setResults] = useState<FoodDto[]>([]);
  const [searched, setSearched] = useState(false);
  const seq = useRef(0);
  const input = useRef<HTMLInputElement>(null);

  // "Alege din bază" on an ingredient puts the cursor here, also when the field is already on screen.
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new label means another ingredient was chosen: focus again
  useEffect(() => {
    if (autoFocus) input.current?.focus();
  }, [autoFocus, label]);

  useEffect(() => {
    const q = query.trim();
    setSearched(false);
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const n = ++seq.current;
    const t = setTimeout(() => {
      api
        .search(q)
        .then((r) => n === seq.current && setResults(r))
        .catch(() => n === seq.current && setResults([]))
        .finally(() => n === seq.current && setSearched(true));
    }, 250);
    return () => clearTimeout(t);
  }, [api, query]);

  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        <Search {...ICON} aria-hidden="true" /> {label}
      </label>
      <input
        ref={input}
        id={id}
        className="field-input"
        placeholder="ex. smântână, cartofi, șuncă Caroli, cod de bare"
        value={query}
        onChange={(e) => onQuery(e.target.value)}
      />
      {results.length > 0 && (
        <ul
          className="card"
          style={{ listStyle: "none", margin: "6px 0 0", padding: 6, maxHeight: 240, overflowY: "auto" }}
        >
          {results.map((f) => (
            <li key={`${f.kind}-${f.id}`}>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ width: "100%", justifyContent: "flex-start", textAlign: "left", height: "auto" }}
                onClick={() => {
                  onPick(f);
                  setResults([]);
                }}
              >
                {foodLabel(f)}
              </button>
            </li>
          ))}
        </ul>
      )}
      {searched && !results.length && (
        <p className="caption muted" style={{ margin: "4px 0 0" }} aria-live="polite">
          Nu am găsit nimic. Încearcă un cuvânt mai simplu (ex. „brânză”, „ceapă”) sau fotografiază eticheta.
        </p>
      )}
    </div>
  );
}
